/**
 * KioskCommunitySelect
 *
 * Pick a community, then choose Play Mode or Settings.
 *
 * summary:
 * - Autocomplete list from `communityData`
 * - Shows basic details for the selected community
 * - Buttons navigate to `/kiosk/:communityId/play` or `/kiosk/:communityId/setting`
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Card,
  CardContent,
  Stack,
  Chip,
  Paper,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Local imports
import { communityData } from "../../utils/communityData";

/**
 * KioskCommunitySelect Component
 *
 * @returns {JSX.Element} The community selection component
 */
export default function KioskCommunitySelect() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  /**
   * Handles community selection
   * @param {Object} value - Selected community object
   */
  const handleCommunitySelect = (value) => {
    setSelected(value);
  };

  /**
   * Renders individual community option in the autocomplete
   * @param {Object} props - Autocomplete option props
   * @param {Object} option - Community data object
   * @returns {JSX.Element} Community option element
   */
  const renderCommunityOption = (props, option) => (
    <Box component="li" {...props}>
      <Stack spacing={1} sx={{ width: "100%" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LocationOnIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography fontWeight={600} variant="body1">
            {option.name}
          </Typography>
          <Chip
            label={option.status}
            color={option.status === "Active" ? "success" : "warning"}
            size="small"
            variant="outlined"
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {option.region}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {option.description}
        </Typography>
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: { xs: 1, sm: 3 },
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ maxWidth: "70%", mx: "auto", width: "100%" }}>
        {/* Welcome Card */}
        <Card
          elevation={8}
          sx={{
            mb: 4,
            border: "2px solid",
            borderColor: "primary.main",
            borderRadius: 3,
            background: "linear-gradient(135deg, #e3f2fd 0%, #f8fcff 100%)",
            "&:hover": {
              boxShadow: "0 8px 25px rgba(33, 150, 243, 0.15)",
            },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <SettingsIcon
                color="primary"
                sx={{
                  fontSize: 32,
                  mt: 0.5,
                  filter: "drop-shadow(0 2px 4px rgba(33, 150, 243, 0.2))",
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    letterSpacing: "-0.5px",
                    mb: 1,
                  }}
                >
                  Kiosk Mode Configuration
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.6,
                    opacity: 0.9,
                    fontSize: "1.1rem",
                  }}
                >
                  Select a community to configure its kiosk slideshow settings.
                  You can set up map displays, station information, and
                  slideshow timers for each community.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Community Selection Card */}
        <Card
          elevation={8}
          sx={{
            border: "2px solid",
            borderColor: selected ? "success.main" : "warning.main",
            borderRadius: 3,
            "&:hover": {
              boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
            },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 3 }}
            >
              <LocationOnIcon color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Select Community
              </Typography>
              <Chip
                label={selected ? "Selected" : "Not Selected"}
                color={selected ? "success" : "warning"}
                size="small"
              />
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, lineHeight: 1.6 }}
            >
              Choose a community from the list below to configure its kiosk
              slideshow settings. Each community can have its own map display,
              station selection, and slideshow configuration.
            </Typography>

            {/* Community Selection Autocomplete */}
            <Autocomplete
              options={communityData}
              getOptionLabel={(option) => option.name}
              renderOption={renderCommunityOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search and select a community"
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              )}
              value={selected}
              onChange={(_, value) => handleCommunitySelect(value)}
              isOptionEqualToValue={(opt, val) =>
                opt.communityId === val?.communityId
              }
              sx={{ mb: 3 }}
            />

            {/* Selected Community Details */}
            {selected && (
              <Paper
                elevation={4}
                sx={{
                  p: 3,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
                >
                  Selected Community Details:
                </Typography>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LocationOnIcon color="primary" sx={{ fontSize: 20 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selected.name}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      label={selected.region}
                      color="info"
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={selected.status}
                      color={
                        selected.status === "Active" ? "success" : "warning"
                      }
                      size="small"
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {selected.description}
                  </Typography>
                </Stack>
              </Paper>
            )}

            {/* Navigation Buttons */}
            {selected && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: "primary.main",
                    textAlign: "center",
                  }}
                >
                  Choose an action for {selected.name}:
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ justifyContent: "center" }}
                >
                  {/* Play Mode Button */}
                  <Paper
                    elevation={4}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%)",
                      border: "1px solid",
                      borderColor: "primary.light",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      flex: 1,
                      maxWidth: 250,
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(33, 150, 243, 0.2)",
                      },
                    }}
                    onClick={() =>
                      navigate(`/kiosk/${selected.communityId}/play`)
                    }
                  >
                    <Stack spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ color: "white", fontWeight: 700 }}
                        >
                          ▶
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "primary.dark" }}
                      >
                        Play Mode
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                      >
                        Launch the kiosk slideshow in full-screen mode for
                        public display
                      </Typography>
                    </Stack>
                  </Paper>

                  {/* Setting Mode Button */}
                  <Paper
                    elevation={4}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, #e8f5e8 0%, #f1f8f1 100%)",
                      border: "1px solid",
                      borderColor: "success.light",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      flex: 1,
                      maxWidth: 250,
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(76, 175, 80, 0.2)",
                      },
                    }}
                    onClick={() =>
                      navigate(`/kiosk/${selected.communityId}/setting`)
                    }
                  >
                    <Stack spacing={2} alignItems="center">
                      <SettingsIcon
                        color="success"
                        sx={{
                          fontSize: 32,
                          filter:
                            "drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3))",
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "success.dark" }}
                      >
                        Setting Mode
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                      >
                        Configure slideshow settings, maps, and station displays
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
